import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSessionAppointment } from '@hooks/useSessionAppointment';
import useSessionNoteChangeHandler from '@hooks/useSessionNoteChangeHandler';
import { useAudioTranscription } from '@hooks/useAudioTranscription';
import NoteEditor from '@components/Clinicians/Session/Components/NoteEditor';
import useClinicalNotesMeta from '@hooks/useClinicalNotesMeta';
import { SessionFormItem } from './SessionFormItem';
import { useHeidiEnabled } from '@hooks/useHeidiEnabled';
import { useHeidiSession } from '../hooks/useHeidiSession';
import useCallStatus from '@hooks/useCallStatus';

const AgreedPlanFormItem = () => {
  const { t } = useTranslation('common');
  const { isAudioError } = useAudioTranscription();

  const { appointment } = useSessionAppointment();
  const videoCallStatus = useCallStatus(appointment);
  const { read_only: readOnly, appointment_completed: apptCompleted, appointment_id: apptId } = appointment;

  const aiNotesAppt = useHeidiEnabled(apptId);

  const { status: aiNotesStatus, notes, hasHeidiError } = useHeidiSession();
  const handleNoteChange = useSessionNoteChangeHandler({ field: 'PLAN' });
  const hasSyncedRef = useRef(false);

  useEffect(() => {
    if (aiNotesStatus === 'completed' && notes?.plan && !hasSyncedRef.current) {
      hasSyncedRef.current = true;
      handleNoteChange({ field: 'PLAN', value: notes.plan });
    }
  }, [aiNotesStatus, notes?.plan]);

  const { getIsFieldRequired, getNoteLabel } = useClinicalNotesMeta();
  const isRequired = getIsFieldRequired('PLAN');

  const shouldHideNotes = () => {
    if (!aiNotesAppt) return false;
    if (videoCallStatus === 'completed' && aiNotesStatus === 'idle') return false;
    return !isAudioError && !hasHeidiError && !apptCompleted && aiNotesStatus !== 'completed';
  };

  const hideNotes = shouldHideNotes();

  return (
    <SessionFormItem
      data-testid="clinical-plan"
      key="plan-form-item"
      label={getNoteLabel('PLAN')}
      required={isRequired}
      labelCol={{ span: 24 }}
      hidden={hideNotes}
    >
      <NoteEditor
        data-testid="clinical-plan"
        field="PLAN"
        ariaLabel={getNoteLabel('PLAN')}
        placeholder={t('clinicalPLANPlaceholder')}
        readOnly={readOnly || apptCompleted}
        onChange={handleNoteChange}
      />
    </SessionFormItem>
  );
};

export default AgreedPlanFormItem;
